import constants from '@Common/Core/Constant';
import { ViewType } from '@Common/Enums/Enum';
import pointerHelper from '@Common/Helpers/PointerHelper';
import { UrlParser } from '@Common/Helpers/UrlParser';

let passiveSupported = false;
try {
    window.addEventListener('passivetest', null, Object.defineProperty({}, 'passive', { get: () => (passiveSupported = true) }));
} catch (err) {
    void 0;
}

export const properties = {
    isDownloading: false,
    get isDebug(): boolean {
        return altosoft.isDebug;
    },
    get isServerDebug(): boolean {
        return !altosoft.model || altosoft.model.isDebug;
    },
    get isProfile(): boolean {
        return altosoft.isProfile;
    },
    get isPrint(): boolean {
        return UrlParser.parseParams(document.location.search).has(constants.isPrintParameter);
    },
    get isCypress(): boolean {
        return window.Cypress != null;
    },
    get iOSVersion(): number | false {
        return (
            parseFloat(
                ('' + (/CPU.*OS ([0-9_]{1,5})|(CPU like).*AppleWebKit.*Mobile/i.exec(navigator.userAgent) || [0, ''])[1])
                    .replace('undefined', '3_2')
                    .replace('_', '.')
                    .replace('_', '')
            ) || false
        );
    },
    get isPassiveSupported(): boolean {
        return passiveSupported;
    },
    get hasOrientationChange(): boolean {
        return 'onorientationchange' in window;
    },
    get isIPad(): boolean {
        return navigator.userAgent.toLowerCase().indexOf('ipad') > -1;
    },
    get isIPhone(): boolean {
        return navigator.userAgent.toLowerCase().indexOf('iphone') > -1;
    },
    get isIPod(): boolean {
        return navigator.userAgent.toLowerCase().indexOf('ipod') > -1;
    },
    get isAndroid(): boolean {
        return navigator.userAgent.toLowerCase().indexOf('android') > -1;
    },
    get isLocalApp(): boolean {
        return window.location.href.indexOf('file://') === 0;
    },
    get isStandalone(): boolean {
        return window.navigator.standalone;
    },
    get isDesktopBrowser(): boolean {
        return (
            properties.isPrint || !(properties.isIPad || properties.isIPhone || properties.isIPod || properties.isAndroid || pointerHelper.hasTouch)
        );
    },
    get viewType(): ViewType {
        let client;

        if (properties.isDesktopBrowser) {
            client = ViewType.pc;
        } else {
            if (properties.isIPad) {
                client = ViewType.tablet;
            } else if (properties.isIPhone || properties.isIPod) {
                client = ViewType.phone;
            } else {
                const ratio = window.devicePixelRatio || 1;
                const height = Math.min(screen.width * ratio, screen.height * ratio);
                if (ratio > 2 || (ratio > 1.5 && ratio <= 2 && height < 900) || (ratio === 1.5 && height < 550) || height < 600) {
                    client = ViewType.phone;
                } else {
                    client = ViewType.tablet;
                }
            }
        }

        return client;
    },
    get lostFocusEventName(): string {
        return 'lostFocus';
    },
    get transparentFillColor(): string {
        return 'rgba(192, 192, 192, 0.0001)';
    },
    get cssPrefix(): string {
        for (const vendor of ['webkitT', 'MozT', 'msT', 'OT']) {
            if (vendor + 'ransform' in document.body.style) {
                return '-' + vendor.slice(0, -1).toLowerCase() + '-';
            }
        }

        return '';
    },
    get browser(): IBrowserInformation {
        const ua = navigator.userAgent.toLowerCase();

        const match =
            /(chrome)[ \/]([\w.]+)/.exec(ua) ||
            /(webkit)[ \/]([\w.]+)/.exec(ua) ||
            /(opera)(?:.*version|)[ \/]([\w.]+)/.exec(ua) ||
            /(msie) ([\w.]+)/.exec(ua) ||
            /(trident)(?:.*rv:([\w.]+))?/.exec(ua) ||
            (ua.indexOf('compatible') < 0 && /(mozilla)(?:.*? rv:([\w.]+)|)/.exec(ua)) ||
            [];

        const browser = {} as IBrowserInformation;

        if (match[1]) {
            browser[match[1]] = true;
            browser.fullVersion = match[2] || '0';
            browser.version = parseFloat(browser.fullVersion);
        }

        // Change Trident engine to MSIE.
        if (browser.trident) {
            browser.msie = true;
        }

        // Chrome is Webkit, but Webkit is also Safari.
        if (browser.chrome) {
            browser.webkit = true;
        } else if (browser.webkit) {
            browser.safari = true;
        } else if (browser.msie) {
            browser.compatibilityMode = document.documentMode !== browser.version;
        }

        return browser;
    },
    get maxInteger(): number {
        return Math.pow(2, 53) - 1;
    },
    get minInteger(): number {
        return -Math.pow(2, 53) + 1;
    }
};

export interface IBrowserInformation {
    compatibilityMode: boolean;
    safari: boolean;
    webkit: boolean;
    chrome: boolean;
    msie: boolean;
    trident: boolean;
    version: number;
    fullVersion: string;
    mozilla?: boolean;
}
